'use client';

import LocaleLink from '@client/components/LocaleLink';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { HUB_COPY, type Solution } from '../_content';
import type { MarketingLocale } from '@shared/locale';

/**
 * A single solution page.
 *
 * One template for all of them: the copy is data (see `_content.ts`), so adding
 * a solution is a data change rather than a new component. Everything renders
 * from props on the server, so the whole page — headings, body, requirements —
 * is in the initial HTML.
 */
export default function SolutionDetailContent({
  locale,
  solution,
  others,
}: {
  locale: MarketingLocale;
  solution: Solution;
  others: Solution[];
}) {
  const copy = HUB_COPY[locale];

  return (
    <div data-ra-landing className="min-h-screen bg-slate-50 dark:bg-[#080a0f]">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <LocaleLink
          href="/solutions"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors mb-8"
        >
          <ArrowLeft size={14} /> {copy.backToHub}
        </LocaleLink>

        <header className="mb-12">
          <div className="text-3xl mb-4" aria-hidden="true">
            {solution.icon}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            {solution.title}
          </h1>
          <p className="text-base sm:text-lg text-slate-500 dark:text-slate-400 leading-relaxed">
            {solution.lede}
          </p>
        </header>

        {solution.sections.map((section) => (
          <section key={section.heading} className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {section.heading}
            </h2>
            <p className="text-[15px] text-slate-600 dark:text-slate-400 leading-relaxed">
              {section.body}
            </p>
            {section.points && (
              <ul className="mt-4 space-y-2">
                {section.points.map((point) => (
                  <li key={point} className="flex gap-2.5 text-[15px] text-slate-600 dark:text-slate-400">
                    <Check
                      size={16}
                      className="flex-shrink-0 mt-1 text-orange-500"
                      aria-hidden="true"
                    />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {/*
          Stated plainly rather than buried: every one of these depends on an
          account, a credential or a configuration the restaurant supplies. The
          site's own /security page takes the same line about not claiming more
          than is in place.
        */}
        <section className="mb-12 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <h2 className="text-base font-bold text-slate-900 dark:text-white mb-3">
            {copy.requirementsHeading}
          </h2>
          <ul className="space-y-2">
            {solution.requirements.map((requirement) => (
              <li
                key={requirement}
                className="text-sm text-slate-600 dark:text-slate-400 pl-4 relative before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-slate-300 dark:before:bg-slate-600"
              >
                {requirement}
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 text-center mb-12">
          <h2 className="font-bold text-slate-900 dark:text-white mb-2">{copy.ctaTitle}</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{copy.ctaBody}</p>
          <LocaleLink
            href="/contact"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors"
          >
            {copy.ctaLink} <ArrowRight size={14} />
          </LocaleLink>
        </section>

        {/* Contextual in-body links — the audit found the site had none. */}
        {others.length > 0 && (
          <nav aria-label={copy.backToHub}>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white mb-3">
              {copy.backToHub}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {others.map((other) => (
                <LocaleLink
                  key={other.slug}
                  href={`/solutions/${other.slug}`}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-slate-600 dark:text-slate-400 hover:border-orange-200 dark:hover:border-orange-900/40 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                >
                  <span aria-hidden="true">{other.icon}</span>
                  <span>{other.title}</span>
                </LocaleLink>
              ))}
            </div>
          </nav>
        )}
      </article>
    </div>
  );
}
